// Lógica pura de Administración · Contratistas (T-0qrc): el borrador del
// formulario, qué va en el PATCH (sólo lo que cambió), qué va en el POST,
// el copy de los toasts y el mapa de códigos de error a frase. Sin React,
// para probarla sola.
//
// RUT y «Dónde trabaja» del handoff §8 no están: la tabla providers no
// tiene esas columnas y esta ronda no migra, así que el borrador tampoco
// los conoce (regla «hide unusable actions»).

import type {
  ProviderCreateBody,
  ProviderListItem,
  ProviderPatchBody,
  ProviderStatus,
} from "@bitacorapro/api-types";
import { ApiError } from "../../../lib/api/error";
import { canReadProviderRoster } from "../../../lib/capabilities";

/** Quién LEE la nómina: el espejo del gate de GET /providers. Vive en
 *  lib/capabilities.ts (canReadProviderRoster) porque la ruta en App.tsx
 *  y la hija del menú en lib/nav.ts leen el mismo predicado que esta
 *  pantalla; aquí queda con el nombre de la sección. */
export const canReadContratistas = canReadProviderRoster;

export type ContratistaDraft = {
  shortName: string;
  legalName: string;
  specialty: string;
  contactEmail: string;
  contactPhone: string;
};

export type DraftField = keyof ContratistaDraft;
export type DraftErrors = Partial<Record<DraftField, string>>;

export const EMPTY_DRAFT: ContratistaDraft = {
  shortName: "",
  legalName: "",
  specialty: "",
  contactEmail: "",
  contactPhone: "",
};

export function draftFrom(item: ProviderListItem): ContratistaDraft {
  return {
    shortName: item.shortName,
    legalName: item.legalName ?? "",
    specialty: item.specialty ?? "",
    contactEmail: item.contactEmail ?? "",
    contactPhone: item.contactPhone ?? "",
  };
}

// Mismo piso que emailSchema del wire: evita el viaje por lo obviamente
// mal escrito; la validación real es del servidor.
export function isValidEmail(v: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

const DRAFT_FIELDS: DraftField[] = [
  "shortName",
  "legalName",
  "specialty",
  "contactEmail",
  "contactPhone",
];

function trimmed(draft: ContratistaDraft): ContratistaDraft {
  return {
    shortName: draft.shortName.trim(),
    legalName: draft.legalName.trim(),
    specialty: draft.specialty.trim(),
    contactEmail: draft.contactEmail.trim(),
    contactPhone: draft.contactPhone.trim(),
  };
}

/** Qué cambió respecto de la ficha actual. `body` es null cuando no hay
 *  nada que mandar o algo está mal. El PATCH no sabe borrar un campo (el
 *  wire no acepta null ni vacío), así que vaciar uno que tenía valor es
 *  un error inline, no un «sin cambios» silencioso. */
export function patchDiff(
  item: ProviderListItem,
  draft: ContratistaDraft
): { body: ProviderPatchBody | null; errors: DraftErrors } {
  const current = draftFrom(item);
  const next = trimmed(draft);
  const errors: DraftErrors = {};
  const body: ProviderPatchBody = {};
  for (const field of DRAFT_FIELDS) {
    if (next[field] === current[field]) continue;
    if (next[field] === "") {
      errors[field] =
        field === "shortName"
          ? "El nombre no puede quedar vacío."
          : "Este dato no se puede borrar.";
      continue;
    }
    if (field === "contactEmail" && !isValidEmail(next.contactEmail)) {
      errors.contactEmail = "Ingresa un correo válido.";
      continue;
    }
    body[field] = next[field];
  }
  const hasErrors = Object.keys(errors).length > 0;
  const hasChanges = Object.keys(body).length > 0;
  return { body: !hasErrors && hasChanges ? body : null, errors };
}

/** El alta: nombre y al menos una forma de contacto (regla del wire). Los
 *  opcionales vacíos no viajan. */
export function createBody(draft: ContratistaDraft): {
  body: ProviderCreateBody | null;
  errors: DraftErrors;
} {
  const next = trimmed(draft);
  const errors: DraftErrors = {};
  if (next.contactEmail && !isValidEmail(next.contactEmail)) {
    errors.contactEmail = "Ingresa un correo válido.";
  }
  const complete =
    next.shortName.length > 0 && (next.contactEmail.length > 0 || next.contactPhone.length > 0);
  if (!complete || Object.keys(errors).length > 0) return { body: null, errors };
  return {
    body: {
      shortName: next.shortName,
      ...(next.legalName ? { legalName: next.legalName } : {}),
      ...(next.specialty ? { specialty: next.specialty } : {}),
      ...(next.contactEmail ? { contactEmail: next.contactEmail } : {}),
      ...(next.contactPhone ? { contactPhone: next.contactPhone } : {}),
    },
    errors,
  };
}

export function estadoLabel(status: ProviderStatus): string {
  return status === "activo" ? "Activo" : "Pausado";
}

export type ContratistaWords = {
  /** «Observación» (recipe.labels.finding). */
  finding: string;
  /** «Observaciones» (recipe.labels.findingPlural). */
  findingPlural: string;
};

/** «Contratista desactivado · 3 observaciones abiertas siguen asignadas»:
 *  pausar no desasigna nada, y el toast lo dice con el número que devolvió
 *  el servidor. */
export function desactivadoToast(openAssigned: number, words: ContratistaWords): string {
  const base = "Contratista desactivado";
  if (openAssigned === 0) return `${base} · sin ${words.findingPlural.toLowerCase()} abiertas`;
  if (openAssigned === 1) {
    return `${base} · 1 ${words.finding.toLowerCase()} abierta sigue asignada`;
  }
  return `${base} · ${openAssigned} ${words.findingPlural.toLowerCase()} abiertas siguen asignadas`;
}

/** El alta con `created: false`: la fila ya estaba y se reusó, así que no
 *  se afirma «agregado» sobre algo que no cambió. */
export function creadoToast(shortName: string, created: boolean): string {
  return created
    ? "Contratista agregado"
    : `«${shortName}» ya estaba en la nómina · se mantiene su ficha`;
}

// Códigos que /providers devuelve hoy → la frase que ve el gerente. El
// mensaje crudo del servidor nunca se muestra.
export function saveErrorMessage(err: unknown): string {
  const generic = "No pudimos guardar los cambios. Intenta de nuevo.";
  if (!(err instanceof ApiError)) return generic;
  switch (err.code) {
    case "ADMIN_REQUIRED":
    case "ROLE_READ_ONLY":
      return "Tu rol no puede hacer este cambio.";
    case "PROVIDER_NAME_TAKEN":
      return "Ya hay un contratista con ese nombre.";
    case "PROVIDER_NOT_FOUND":
      return "Este contratista ya no existe. Recarga la página.";
    case "VALIDATION_ERROR":
      return "Revisa los datos ingresados.";
    default:
      return generic;
  }
}
